import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'helios';
type ThemePreference = 'dark' | 'light' | 'helios' | 'system';

interface ThemeContextType {
  theme: Theme;
  preference: ThemePreference;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  setPreference: (pref: ThemePreference) => void;
  isDark: boolean;
}

export const ThemeContext = createContext<ThemeContextType | null>(null);

function getSystemTheme(): Theme {
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light';
  return 'dark';
}

function resolveTheme(pref: ThemePreference): Theme {
  if (pref === 'system') return getSystemTheme();
  return pref;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    const stored = localStorage.getItem('helios_theme_pref') as ThemePreference | null;
    // Default preference is 'light' (Cinder Track parchment) as of v0.3.0.0.
    // Existing users who explicitly chose 'dark', 'helios', or 'system' keep their choice.
    if (stored === 'dark' || stored === 'light' || stored === 'helios' || stored === 'system') {
      return stored;
    }
    return 'light';
  });

  const [theme, setThemeState] = useState<Theme>(() => {
    const resolved = resolveTheme(preference);
    // Set immediately on first load to avoid flash
    document.documentElement.setAttribute('data-theme', resolved);
    return resolved;
  });

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('helios_theme', theme);

    // Apply CSS variable overrides for light mode
    const root = document.documentElement;
    if (theme === 'light') {
      // Cinder Track v3 light palette — parchment canvas + warm ink family.
      // See DESIGN.md "Light Mode (Helios warm — cornsilk)" section.
      root.style.setProperty('--bg-primary', '#F0E3BF');                 // parchment canvas
      root.style.setProperty('--bg-surface-flat', '#E8DAB0');            // warm cream surface (modals)
      root.style.setProperty('--bg-surface', '#E8DAB0');                 // flat, no gradient in v3
      root.style.setProperty('--bg-input', 'rgba(43, 36, 20, 0.06)');    // subtle ink overlay on parchment
      root.style.setProperty('--text-primary', '#2B2414');               // ink-1
      root.style.setProperty('--text-muted', '#5E5138');                 // ink-2
      root.style.setProperty('--border-subtle', 'rgba(43, 36, 20, 0.18)'); // hairline
      root.style.setProperty('--border-top', 'rgba(43, 36, 20, 0.28)');    // hairline-strong
    } else if (theme === 'helios') {
      root.style.setProperty('--bg-primary', '#0A0804');
      root.style.setProperty('--bg-surface-flat', '#151008');
      root.style.setProperty('--bg-surface', 'linear-gradient(160deg, #151008, #100C06)');
      root.style.setProperty('--bg-input', '#1A1408');
      root.style.setProperty('--text-primary', 'var(--color-text-primary)');
      root.style.setProperty('--text-muted', '#A08860');
      root.style.setProperty('--border-subtle', 'rgba(232,160,32,0.1)');
      root.style.setProperty('--border-top', 'rgba(232,160,32,0.15)');
    } else {
      root.style.setProperty('--bg-primary', 'var(--color-canvas)');
      root.style.setProperty('--bg-surface-flat', 'var(--color-surface-ephemeral)');
      root.style.setProperty('--bg-surface', 'linear-gradient(160deg, var(--color-surface-ephemeral), #101010)');
      root.style.setProperty('--bg-input', 'var(--color-surface-ephemeral)');
      root.style.setProperty('--text-primary', 'var(--color-text-primary)');
      root.style.setProperty('--text-muted', 'var(--color-text-secondary)');
      root.style.setProperty('--border-subtle', 'rgba(255,255,255,0.07)');
      root.style.setProperty('--border-top', 'rgba(255,255,255,0.10)');
    }
  }, [theme]);

  // Listen for system theme changes when preference is 'system'
  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => setThemeState(getSystemTheme());
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  const setPreference = (pref: ThemePreference) => {
    setPreferenceState(pref);
    localStorage.setItem('helios_theme_pref', pref);
    setThemeState(resolveTheme(pref));
  };

  const toggleTheme = () => {
    const order: Theme[] = ['dark', 'light', 'helios'];
    const next = order[(order.indexOf(theme) + 1) % order.length];
    setPreference(next);
  };
  const setTheme = (t: Theme) => { setPreferenceState(t); setThemeState(t); localStorage.setItem('helios_theme_pref', t); };

  return (
    <ThemeContext.Provider value={{ theme, preference, toggleTheme, setTheme, setPreference, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
